import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Select from "react-select";
import apiClient from "../api/apiClient";

const asText = (v) => (v === null || v === undefined ? "" : String(v));

const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/\./g, "").replace(/,/g, "."));
  // Nota: en PY suele venir 1.234,56 -> arriba normalizamos.
  return Number.isNaN(n) ? null : n;
};

const excelDateToIso = (v) => {
  if (!v) return "";

  // Si es número, probablemente sea serial de Excel.
  if (typeof v === "number") {
    const date = XLSX.SSF.parse_date_code(v);
    if (date) {
      const yyyy = String(date.y).padStart(4, "0");
      const mm = String(date.m).padStart(2, "0");
      const dd = String(date.d).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}T00:00:00Z`;
    }
  }

  const s = String(v).trim();

  // DD/MM/AAAA
  const m1 = s.match(/^([0-3]?\d)\/([01]?\d)\/(\d{4})$/);
  if (m1) {
    const dd = m1[1].padStart(2, "0");
    const mm = m1[2].padStart(2, "0");
    const yyyy = m1[3];
    return `${yyyy}-${mm}-${dd}T00:00:00Z`;
  }

  // AAAA-MM-DD o ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const yyyy = s.slice(0, 4);
    const mm = s.slice(5, 7);
    const dd = s.slice(8, 10);
    return `${yyyy}-${mm}-${dd}T00:00:00Z`;
  }

  // fallback: intentamos Date
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T00:00:00Z`;
};

const isoToYmd = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  // si ya viene YYYY-MM-DD...
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO típico: YYYY-MM-DDTHH:MM:SSZ
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return s;
};

const addMonthsYmd = (ymd, monthsToAdd) => {
  if (!ymd) return "";
  const base = isoToYmd(ymd);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(base)) return base;
  const [y, m, d] = base.split("-").map((x) => Number(x));
  if (!y || !m || !d) return base;

  const dt = new Date(Date.UTC(y, m - 1, d));
  // setUTCMonth maneja overflow, pero puede recortar días al fin de mes (comportamiento deseado)
  dt.setUTCMonth(dt.getUTCMonth() + monthsToAdd);
  const yyyy = String(dt.getUTCFullYear()).padStart(4, "0");
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeHeaderKey = (k) => String(k || "").trim().toLowerCase();

const findCol = (row, names) => {
  const keys = Object.keys(row || {});
  const wanted = names.map((n) => normalizeHeaderKey(n));
  const matchKey = keys.find((k) => wanted.includes(normalizeHeaderKey(k)));
  return matchKey;
};

const GestionInmobiliariaNuevaCotizacion = () => {
  const navigate = useNavigate();

  const [itemsOptions, setItemsOptions] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState("");

  const [unidadId, setUnidadId] = useState(2);
  const [unidadLines, setUnidadLines] = useState([{ U_ItemCode: "", U_PrecioUnitario: 0 }]);
  const [unidadCreating, setUnidadCreating] = useState(false);
  const [unidadError, setUnidadError] = useState("");
  const [unidadResult, setUnidadResult] = useState(null);

  const [header, setHeader] = useState({
    U_ESTADO: "Abierto",
    U_NROCONTRATO: "",
    U_MONEDA: "USD",
    U_PRECIOL: "",
    U_PRECIOVE: "",
    U_ADELA: "",
    U_FECHACO: "",
    U_FECHAV: "",
  });

  const [cuotasCantidad, setCuotasCantidad] = useState(0);
  const [cuotasPlazo, setCuotasPlazo] = useState("MENSUAL");

  const [cuotas, setCuotas] = useState([]);
  const [excelError, setExcelError] = useState("");
  const [cuotasToolError, setCuotasToolError] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const unidadCreada = Boolean(unidadResult?.DocEntry);

  // Si el usuario editó manualmente los precios, no los volvemos a pisar con el total.
  const [precioManual, setPrecioManual] = useState({ pre: false, ven: false });

  const unidadItemSelectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      // Asegura que el input no se "achique" demasiado dentro de la tabla
      container: (base) => ({ ...base, minWidth: 260 }),
    }),
    []
  );

  useEffect(() => {
    let alive = true;
    const loadItems = async () => {
      setItemsLoading(true);
      setItemsError("");
      try {
        const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
        const companyDb = user?.CompanyDB;
        const res = await apiClient.get("/inmobiliaria/unidades-items", {
          params: { CompanyDB: companyDb },
        });
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setItemsOptions(
          list
            .filter((x) => x?.ItemCode)
            .map((x) => ({
              value: String(x.ItemCode),
              label: `${x.ItemCode} - ${x.ItemName || ""}`.trim(),
              itemName: x.ItemName || "",
            }))
        );
      } catch (e) {
        if (!alive) return;
        setItemsError(e?.response?.data?.error || e?.message || "Error cargando unidades");
        setItemsOptions([]);
      } finally {
        if (alive) setItemsLoading(false);
      }
    };
    loadItems();
    return () => {
      alive = false;
    };
  }, []);

  const unidadTotal = useMemo(() => {
    return (unidadLines || []).reduce((acc, l) => acc + (parseNumber(l?.U_PrecioUnitario) || 0), 0);
  }, [unidadLines]);

  // Mantiene U_PRECIOL / U_PRECIOVE alineados al total de la Unidad, excepto si el usuario los tocó.
  useEffect(() => {
    setHeader((h) => {
      const next = { ...h };
      if (!precioManual.pre) next.U_PRECIOL = unidadTotal;
      if (!precioManual.ven) next.U_PRECIOVE = unidadTotal;
      return next;
    });
  }, [unidadTotal, precioManual.pre, precioManual.ven]);

  const onUnidadLineChange = (idx, key, value) => {
    setUnidadLines((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const addUnidadLine = () => {
    setUnidadLines((prev) => [...prev, { U_ItemCode: "", U_PrecioUnitario: 0 }]);
  };

  const removeUnidadLine = (idx) => {
    setUnidadLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const createUnidad = async () => {
    // Evitar duplicados: si ya hay una unidad creada, no permitir recrearla.
    if (unidadResult?.DocEntry) return unidadResult;

    setUnidadCreating(true);
    setUnidadError("");

  // Nota: NO limpiamos `unidadResult` al iniciar, porque si el usuario hace doble click rápido,
  // el estado `unidadCreada` podría volver a false momentáneamente y re-habilitar edición.

    try {
      const payload = {
        U_ID: Number(unidadId) || 0,
        REM_UNI_DCollection: (unidadLines || [])
          .filter((l) => String(l?.U_ItemCode || "").trim() !== "")
          .map((l) => ({
            U_ItemCode: String(l.U_ItemCode || "").trim(),
            U_PrecioUnitario: parseNumber(l.U_PrecioUnitario) || 0,
          })),
      };

      const res = await apiClient.post("/inmobiliaria/unidades", payload);
      setUnidadResult(res.data);

      // Para que se cree la cotización, estos campos deben venir con el total de la unidad.
      // (Si el usuario los editó manualmente antes, este set los vuelve a sincronizar tras crear la unidad.)
      const totalFromUnidad = parseNumber(res.data?.U_PrecioTotal);
      const totalToUse = totalFromUnidad === null ? unidadTotal : totalFromUnidad;
      setHeader((h) => ({
        ...h,
        U_PRECIOL: totalToUse,
        U_PRECIOVE: totalToUse,
      }));
      setPrecioManual({ pre: false, ven: false });

      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Error creando unidad";
      setUnidadError(msg);
      return null;
    } finally {
      setUnidadCreating(false);
    }
  };

  const onExcelFile = async (file) => {
    setExcelError("");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames?.[0];
      const ws = wb.Sheets?.[sheetName];
      if (!ws) throw new Error("No se encontró hoja en el Excel");

      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!Array.isArray(rows) || rows.length === 0) {
        setCuotas([]);
        return;
      }

      const nroKey = findCol(rows[0], ["Nro Cuota", "NroCuota", "U_NROCUO", "NROCUO"]) || "";
      const vencKey = findCol(rows[0], ["Vencimiento", "U_FECHAVTO", "FECHAVTO"]) || "";
      const montoKey = findCol(rows[0], ["Monto", "Cuota", "U_CUOTA", "CUOTA"]) || "";

      if (!nroKey || !vencKey || !montoKey) {
        throw new Error(
          "El Excel debe tener columnas: Nro Cuota, Vencimiento y Monto (pueden variar los nombres, pero deben existir)"
        );
      }

      const parsed = rows
        .map((r) => {
          const nro = asText(r[nroKey]).trim();
          const iso = excelDateToIso(r[vencKey]);
          const monto = parseNumber(r[montoKey]);
          return {
            U_NROCUO: nro,
            U_FECHAVTO: iso,
            U_CUOTA: monto === null ? "" : String(monto),
          };
        })
        .filter((c) => c.U_NROCUO !== "" || c.U_FECHAVTO !== "" || c.U_CUOTA !== "");

      // Ordenamos por nro cuota numérico si puede
      parsed.sort((a, b) => {
        const na = Number(a.U_NROCUO);
        const nb = Number(b.U_NROCUO);
        if (Number.isNaN(na) || Number.isNaN(nb)) return String(a.U_NROCUO).localeCompare(String(b.U_NROCUO));
        return na - nb;
      });

      setCuotas(parsed);
    } catch (e) {
      setExcelError(e?.message || "Error leyendo Excel");
      setCuotas([]);
    }
  };

  const exportCuotasToExcel = () => {
    const rows = (cuotas || []).map((c) => ({
      "Nro Cuota": asText(c?.U_NROCUO),
      Vencimiento: isoToYmd(c?.U_FECHAVTO),
      Monto: asText(c?.U_CUOTA),
    }));

    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ "Nro Cuota": "", Vencimiento: "", Monto: "" }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cuotas");

    const nro = String(header?.U_NROCONTRATO || "").trim();
    const suffix = nro ? `_${nro}` : "";
    XLSX.writeFile(wb, `cuotas${suffix}.xlsx`);
  };

  const saveCotizacion = async () => {
    setSaving(true);
    setSaveError("");

    try {
      // Validación: no crear cotización si aún no existe una unidad creada.
      if (!unidadResult?.DocEntry) {
        throw new Error("Debe crear una unidad antes de crear la cotización.");
      }
      const uni = unidadResult;

      // 2) Crear cotización
      const payload = {
        header: {
          ...header,
          U_COD_LOTE: String(uni.DocEntry),
        },
        cuotas,
      };

      const res = await apiClient.post("/inmobiliaria/cotizaciones", payload);
      const created = res.data;
      const newDocEntry = created?.DocEntry;
      if (newDocEntry) {
        navigate(`/inmobiliaria/cotizaciones/${newDocEntry}`);
        return;
      }

      // fallback si no vuelve DocEntry, volvemos al listado
      navigate("/inmobiliaria/cotizaciones");
    } catch (e) {
      setSaveError(e?.response?.data?.error || e?.message || "Error guardando cotización");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    // default fechas si están vacías
    if (!header.U_FECHACO || !header.U_FECHAV) {
      const now = new Date();
      const yyyy = String(now.getFullYear());
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const iso = `${yyyy}-${mm}-${dd}T00:00:00Z`;
      setHeader((h) => ({
        ...h,
        U_FECHACO: h.U_FECHACO || iso,
        U_FECHAV: h.U_FECHAV || iso,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateCuotas = () => {
    setCuotasToolError("");
    const n = Number(cuotasCantidad) || 0;
    if (n <= 0) {
      setCuotas([]);
      return;
    }

    const precioVenta = parseNumber(header.U_PRECIOVE) || 0;
    const entrega = parseNumber(header.U_ADELA) || 0;
    const base = Math.max(0, precioVenta - entrega);
    const montoCuota = base / n;

    const plazoToMonths = {
      MENSUAL: 1,
      TRIMESTRAL: 3,
      SEMESTRAL: 6,
      ANUAL: 12,
    };
    const stepMonths = plazoToMonths[cuotasPlazo] ?? 1;

    // Reglas de vencimiento:
    // - Con entrega inicial (>0):
    //    * Cuota 0 vence en Fecha Cotización (U_FECHACO) y monto = entrega.
    //    * Cuota 1 vence en Vencimiento (U_FECHAV).
    //    * Cuotas siguientes suman stepMonths sobre U_FECHAV.
    // - Sin entrega inicial:
    //    * Cuota 1 vence en Vencimiento (U_FECHAV).
    //    * Cuotas siguientes suman stepMonths sobre U_FECHAV.
    const hasEntrega = entrega > 0;
    const fechaCotizacion = isoToYmd(header.U_FECHACO);
    const fechaVenc = isoToYmd(header.U_FECHAV);

    if (hasEntrega && !fechaCotizacion) {
      setCuotasToolError("Para generar cuotas con entrega inicial, complete la Fecha Cotización.");
      return;
    }

    if (!fechaVenc) {
      setCuotasToolError("Para generar cuotas, complete el Vencimiento.");
      return;
    }

    const next = [];
    if (hasEntrega) {
      next.push({
        U_NROCUO: "0",
        U_FECHAVTO: fechaCotizacion,
        U_CUOTA: String(entrega),
      });
    }

    for (let i = 1; i <= n; i++) {
      next.push({
        U_NROCUO: String(hasEntrega ? i : i),
        U_FECHAVTO: addMonthsYmd(fechaVenc, (i - 1) * stepMonths),
        U_CUOTA: String(montoCuota),
      });
    }

    setCuotas(next);
  };

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-0">Nueva Cotización</h2>
        </div>
        <Link className="btn btn-outline-secondary" to="/inmobiliaria/cotizaciones">
          Volver
        </Link>
      </div>

      {(unidadError || excelError || saveError) && (
        <div className="alert alert-danger">
          {unidadError && <div>Unidad: {unidadError}</div>}
          {itemsError && <div>Items: {itemsError}</div>}
          {excelError && <div>Excel: {excelError}</div>}
          {saveError && <div>Cotización: {saveError}</div>}
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-header bg-light">
          <strong>Unidad</strong>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-2">
              <label className="form-label">ID</label>
              <input
                className="form-control"
                value={unidadId}
                onChange={(e) => setUnidadId(e.target.value)}
                disabled={unidadCreada || unidadCreating}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Total</label>
              <input className="form-control" value={unidadTotal.toLocaleString("es-PY")} readOnly />
            </div>
            <div className="col-12 col-md-6 text-md-end">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={createUnidad}
                disabled={unidadCreating || unidadCreada}
              >
                {unidadCreada ? "Unidad creada" : unidadCreating ? "Creando..." : "Crear Unidad"}
              </button>
              {unidadResult?.DocEntry && (
                <span className="ms-3 text-muted">Unidad creada: {unidadResult.DocEntry}</span>
              )}
            </div>
          </div>

          <hr />

          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead className="table-primary">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>ItemCode</th>
                  <th className="text-end" style={{ width: 200 }}>
                    Precio Unitario
                  </th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {(unidadLines || []).map((l, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <Select
                        classNamePrefix="react-select"
                        isClearable
                        isDisabled={unidadCreada || unidadCreating}
                        isLoading={itemsLoading}
                        options={itemsOptions}
                        styles={unidadItemSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={
                          l?.U_ItemCode
                            ? itemsOptions.find((o) => o.value === String(l.U_ItemCode)) || {
                                value: String(l.U_ItemCode),
                                label: String(l.U_ItemCode),
                              }
                            : null
                        }
                        onChange={(opt) => onUnidadLineChange(idx, "U_ItemCode", opt?.value || "")}
                        placeholder="Seleccionar..."
                      />
                    </td>
                    <td>
                      <input
                        className="form-control form-control-sm text-end"
                        value={asText(l?.U_PrecioUnitario)}
                        onChange={(e) => onUnidadLineChange(idx, "U_PrecioUnitario", e.target.value)}
                        disabled={unidadCreada || unidadCreating}
                      />
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeUnidadLine(idx)}
                        disabled={unidadCreada || unidadCreating || (unidadLines || []).length <= 1}
                      >
                        X
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={addUnidadLine}
            disabled={unidadCreada || unidadCreating}
          >
            + Agregar línea
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-header bg-light">
          <strong>Cabecera de cotización</strong>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md">
              <label className="form-label">Estado</label>
              <input
                className="form-control"
                value={asText(header.U_ESTADO)}
                onChange={(e) => setHeader((h) => ({ ...h, U_ESTADO: e.target.value }))}
              />
            </div>
            <div className="col-12 col-md">
              <label className="form-label">Moneda</label>
              <input
                className="form-control"
                value={asText(header.U_MONEDA)}
                onChange={(e) => setHeader((h) => ({ ...h, U_MONEDA: e.target.value }))}
              />
            </div>
            <div className="col-12 col-md">
              <label className="form-label">Nro Contrato</label>
              <input
                className="form-control"
                value={asText(header.U_NROCONTRATO)}
                onChange={(e) => setHeader((h) => ({ ...h, U_NROCONTRATO: e.target.value }))}
              />
            </div>
            <div className="col-12 col-md">
              <label className="form-label">Fecha Cotización</label>
              <input
                className="form-control"
                type="date"
                value={asText(header.U_FECHACO)}
                onChange={(e) => setHeader((h) => ({ ...h, U_FECHACO: e.target.value }))}
              />
            </div>
            <div className="col-12 col-md">
              <label className="form-label">Vencimiento</label>
              <input
                className="form-control"
                type="date"
                value={asText(header.U_FECHAV)}
                onChange={(e) => setHeader((h) => ({ ...h, U_FECHAV: e.target.value }))}
              />
            </div>
          </div>

          <div className="row g-3 align-items-end mt-1">
            <div className="col-12 col-md-3">
              <label className="form-label">Precio lista</label>
              <input
                className="form-control"
                value={asText(header.U_PRECIOL)}
                onChange={(e) => {
                  setPrecioManual((p) => ({ ...p, pre: true }));
                  setHeader((h) => ({ ...h, U_PRECIOL: e.target.value }));
                }}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Precio de venta</label>
              <input
                className="form-control"
                value={asText(header.U_PRECIOVE)}
                onChange={(e) => {
                  setPrecioManual((p) => ({ ...p, ven: true }));
                  setHeader((h) => ({ ...h, U_PRECIOVE: e.target.value }));
                }}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Entrega inicial</label>
              <input
                className="form-control"
                value={asText(header.U_ADELA)}
                onChange={(e) => setHeader((h) => ({ ...h, U_ADELA: e.target.value }))}
              />
            </div>
          </div>

          <hr />

          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label">Cantidad de cuotas</label>
              <input
                className="form-control"
                type="number"
                min="0"
                value={asText(cuotasCantidad)}
                onChange={(e) => setCuotasCantidad(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">Plazo</label>
              <select
                className="form-select"
                value={cuotasPlazo}
                onChange={(e) => setCuotasPlazo(e.target.value)}
              >
                <option value="MENSUAL">Mensual</option>
                <option value="TRIMESTRAL">Trimestral</option>
                <option value="SEMESTRAL">Semestral</option>
                <option value="ANUAL">Anual</option>
              </select>
            </div>

            <div className="col-12 col-md-6 text-md-end">
              <button type="button" className="btn btn-outline-primary" onClick={generateCuotas}>
                Generar cuotas
              </button>
              <div className="form-text text-md-end">
                Monto = (Precio venta - Entrega inicial) / Cantidad
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <strong>Cuotas</strong>
          <div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm me-2"
              onClick={exportCuotasToExcel}
              disabled={(cuotas || []).length === 0}
            >
              Exportar Excel
            </button>
            <label className="btn btn-outline-secondary btn-sm mb-0">
              Importar Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={(e) => onExcelFile(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
        <div className="card-body">
          {cuotasToolError && <div className="alert alert-warning mb-3">{cuotasToolError}</div>}
          {cuotas.length === 0 ? (
            <div className="text-muted">Aún no hay cuotas. Importá un Excel.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead className="table-primary">
                  <tr>
                    <th style={{ width: 120 }}>Nro Cuota</th>
                    <th style={{ width: 220 }}>Vencimiento</th>
                    <th className="text-end">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {cuotas.map((c, idx) => (
                    <tr key={idx}>
                      <td>{asText(c.U_NROCUO)}</td>
                      <td>{asText(c.U_FECHAVTO)}</td>
                      <td className="text-end">{asText(c.U_CUOTA)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={saveCotizacion}
          disabled={saving || !unidadCreada}
          title={!unidadCreada ? "Primero cree la unidad para poder guardar la cotización" : ""}
        >
          {saving ? "Guardando..." : "Guardar Cotización"}
        </button>
      </div>
    </div>
  );
};

export default GestionInmobiliariaNuevaCotizacion;
